// src/actions/hdfcRetailActions.js
import axios from 'axios';
import {
    GET_ALL_HDFC_RETAIL_REQUEST,
    GET_ALL_HDFC_RETAIL_SUCCESS,
    GET_ALL_HDFC_RETAIL_FAIL,
    IMPORT_HDFC_RETAIL_REQUEST,
    IMPORT_HDFC_RETAIL_SUCCESS,
    IMPORT_HDFC_RETAIL_FAIL,
    EXPORT_HDFC_RETAIL_REQUEST,
    EXPORT_HDFC_RETAIL_SUCCESS,
    EXPORT_HDFC_RETAIL_FAIL,
    UPDATE_HDFC_RETAIL_REQUEST,
    UPDATE_HDFC_RETAIL_SUCCESS,
    UPDATE_HDFC_RETAIL_FAIL,
    DELETE_HDFC_RETAIL_REQUEST,
    DELETE_HDFC_RETAIL_SUCCESS,
    DELETE_HDFC_RETAIL_FAIL,
    BULK_DELETE_HDFC_RETAIL_REQUEST,
    BULK_DELETE_HDFC_RETAIL_SUCCESS,
    BULK_DELETE_HDFC_RETAIL_FAIL,
} from '../constants/hdfcretailConstants';
import { logout } from './userActions';

const baseUrl = process.env.REACT_APP_API_URL;

// Get HDFC Retail records with pagination and search
export const getHdfcRetailRecords = (queryParams = '') => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_ALL_HDFC_RETAIL_REQUEST });

        const {
            userLogin: { userInfo },
        } = getState();

        // Parse and validate query parameters
        const params = new URLSearchParams(queryParams);
        const page = Math.max(1, parseInt(params.get('page')) || 1);
        const limit = Math.max(1, parseInt(params.get('limit')) || 10);
        const search = params.get('search') || '';
        const importDate = params.get('importDate') || '';
        const status = params.get('status') || '';
        const losLsMailFilter = params.get('losLsMailFilter') || '';
        const fosFilter = params.get('fosFilter') || '';
        const dept = params.get('dept') || '';

        // Rebuild clean query string
        let cleanQuery = `search=${encodeURIComponent(search)}&page=${page}&limit=${limit}`;
        if (importDate) {
            cleanQuery += `&importDate=${encodeURIComponent(importDate)}`;
        }
        if (status) {
            cleanQuery += `&status=${encodeURIComponent(status)}`;
        }
        if (losLsMailFilter) {
            cleanQuery += `&losLsMailFilter=${encodeURIComponent(losLsMailFilter)}`;
        }
        if (fosFilter) {
            cleanQuery += `&fosFilter=${encodeURIComponent(fosFilter)}`;
        }
        if (dept) {
            cleanQuery += `&dept=${encodeURIComponent(dept)}`;
        }

        console.log('========== API ACTION DEBUG ==========');
        console.log('Original queryParams:', queryParams);
        console.log('Parsed params:', { search, page, limit, importDate, status, losLsMailFilter, fosFilter, dept });
        console.log('Clean query to be sent:', cleanQuery);
        console.log('======================================');

        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.get(`${baseUrl}/hdfc-retail?${cleanQuery}`, config);

        dispatch({
            type: GET_ALL_HDFC_RETAIL_SUCCESS,
            payload: data,
        });
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: GET_ALL_HDFC_RETAIL_FAIL,
            payload: error.response && error.response.data.message
                ? error.response.data.message
                : error.message,
        });
    }
};

// Import HDFC Retail records from file
export const importHdfcRetailRecords = (formData) => async (dispatch, getState) => {
    try {
        dispatch({ type: IMPORT_HDFC_RETAIL_REQUEST });

        const {
            userLogin: { userInfo },
        } = getState();

        const config = {
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.post(`${baseUrl}/hdfc-retail/import`, formData, config);

        dispatch({
            type: IMPORT_HDFC_RETAIL_SUCCESS,
            payload: data,
        });

        return data; // Return data on success
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        const errorMessage = error.response && error.response.data.message
            ? error.response.data.message
            : error.message;

        dispatch({
            type: IMPORT_HDFC_RETAIL_FAIL,
            payload: errorMessage,
        });

        // Throw the error so the component can catch it and show the message
        throw new Error(errorMessage);
    }
};

// Export HDFC Retail records to file
export const exportHdfcRetailRecords = () => async (dispatch, getState) => {
    try {
        dispatch({ type: EXPORT_HDFC_RETAIL_REQUEST });

        const {
            userLogin: { userInfo },
        } = getState();

        const config = {
            headers: {
                Authorization: `Bearer ${userInfo.token}`,
            },
            responseType: 'blob',
        };

        const { data, headers } = await axios.get(`${baseUrl}/hdfc-retail/export`, config);

        // Try to get filename from content-disposition headerz
        let filename = 'HDFC_Retail_Export.xlsx';
        const disposition = headers && headers['content-disposition'];
        if (disposition && disposition.indexOf('filename=') !== -1) {
            filename = disposition.split('filename=')[1].replace(/['"]/g, '');
        }

        // Create download link
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        dispatch({
            type: EXPORT_HDFC_RETAIL_SUCCESS,
            payload: 'Export completed successfully',
        });
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: EXPORT_HDFC_RETAIL_FAIL,
            payload: error.response && error.response.data.message
                ? error.response.data.message
                : error.message,
        });
    }
};

// Update HDFC Retail record
export const updateHdfcRetailRecord = (id, recordData) => async (dispatch, getState) => {
    try {
        dispatch({ type: UPDATE_HDFC_RETAIL_REQUEST });

        const {
            userLogin: { userInfo },
        } = getState();

        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.put(`${baseUrl}/hdfc-retail/${id}`, recordData, config);

        dispatch({
            type: UPDATE_HDFC_RETAIL_SUCCESS,
            payload: data,
        });
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: UPDATE_HDFC_RETAIL_FAIL,
            payload: error.response && error.response.data.message
                ? error.response.data.message
                : error.message,
        });
    }
};

// Delete HDFC Retail record
export const deleteHdfcRetailRecord = (id) => async (dispatch, getState) => {
    try {
        dispatch({ type: DELETE_HDFC_RETAIL_REQUEST });

        const {
            userLogin: { userInfo },
        } = getState();

        const config = {
            headers: {
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        await axios.delete(`${baseUrl}/hdfc-retail/${id}`, config);

        dispatch({
            type: DELETE_HDFC_RETAIL_SUCCESS,
            payload: id,
        });
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: DELETE_HDFC_RETAIL_FAIL,
            payload: error.response && error.response.data.message
                ? error.response.data.message
                : error.message,
        });
    }
};

// Bulk delete HDFC Retail records
export const bulkDeleteHdfcRetailRecords = (ids) => async (dispatch, getState) => {
    try {
        dispatch({ type: BULK_DELETE_HDFC_RETAIL_REQUEST });

        const {
            userLogin: { userInfo },
        } = getState();

        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.post(`${baseUrl}/hdfc-retail/bulk-delete`, { ids }, config);

        dispatch({
            type: BULK_DELETE_HDFC_RETAIL_SUCCESS,
            payload: { deletedCount: data.deletedCount },
        });
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: BULK_DELETE_HDFC_RETAIL_FAIL,
            payload: error.response && error.response.data.message
                ? error.response.data.message
                : error.message,
        });
    }
};

export const syncHdfcRetailTaskStatus = () => async (dispatch, getState) => {
    try {
        dispatch({
            type: 'SYNC_HDFC_RETAIL_TASK_STATUS_REQUEST',
        });

        const {
            userLogin: { userInfo },
        } = getState();

        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo?.token}`,
            },
        };

        const { data } = await axios.post(
            `${baseUrl}/hdfc-retail/sync-status`,
            {},
            config
        );

        dispatch({
            type: 'SYNC_HDFC_RETAIL_TASK_STATUS_SUCCESS',
            payload: data,
        });

        return data; // Return data including updatedRecords
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: 'SYNC_HDFC_RETAIL_TASK_STATUS_FAIL',
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
        throw error;
    }
};

// Add this function at the end of your HdfcRetailAction.js file
export const getFieldExecutives = (search = '') => async (dispatch, getState) => {
    try {
        dispatch({
            type: 'GET_FIELD_EXECUTIVES_REQUEST',
        });

        const {
            userLogin: { userInfo },
        } = getState();

        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo?.token}`,
            },
        };

        const { data } = await axios.get(
            `${baseUrl}/hdfc-retail/field-executives?search=${encodeURIComponent(search)}`,
            config
        );

        dispatch({
            type: 'GET_FIELD_EXECUTIVES_SUCCESS',
            payload: data,
        });

        return data; // Return the data
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: 'GET_FIELD_EXECUTIVES_FAIL',
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
        throw error; // Throw error so it can be caught
    }
};

export const getHdfcRetailFOS = (importDate = '') => async (dispatch, getState) => {
    try {
        dispatch({
            type: 'GET_FOS_REQUEST',
        });

        const {
            userLogin: { userInfo },
        } = getState();

        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo?.token}`,
            },
        };

        // Build URL with importDate query parameter if provided
        let url = `${baseUrl}/hdfc-retail/fos`;
        if (importDate) {
            url += `?importDate=${encodeURIComponent(importDate)}`;
        }

        const { data } = await axios.get(url, config);

        dispatch({
            type: 'GET_FOS_SUCCESS',
            payload: data,
        });

        return data;
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: 'GET_FOS_FAIL',
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
        throw error;
    }
};

export const getHdfcRetailDepartments = (importDate = '') => async (dispatch, getState) => {
    try {
        dispatch({
            type: 'GET_DEPARTMENTS_REQUEST',
        });

        const {
            userLogin: { userInfo },
        } = getState();

        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo?.token}`,
            },
        };

        // Build URL with importDate query parameter if provided
        let url = `${baseUrl}/hdfc-retail/departments`;
        if (importDate) {
            url += `?importDate=${encodeURIComponent(importDate)}`;
        }

        const { data } = await axios.get(url, config);

        dispatch({
            type: 'GET_DEPARTMENTS_SUCCESS',
            payload: data,
        });

        return data;
    } catch (error) {
        if (error.response?.status === 401) {
            dispatch(logout());
            return;
        }
        dispatch({
            type: 'GET_DEPARTMENTS_FAIL',
            payload:
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
        });
        throw error;
    }
};